import { maskCnpj, onlyDigits, validateCnpj } from '@/components/CpfCnpjInput/utils'
import type { RaspadinhaParticipatingCompany } from '@/types/raspadinha-campaign'

function buildParticipatingCompanyId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `company-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function normalizeParticipatingCompanyCnpj(value: string): string {
  return onlyDigits(value)
}

export function formatParticipatingCompanyCnpj(value: string): string {
  return maskCnpj(normalizeParticipatingCompanyCnpj(value))
}

export function createParticipatingCompany(cnpj: string): RaspadinhaParticipatingCompany {
  return {
    id: buildParticipatingCompanyId(),
    cnpj: formatParticipatingCompanyCnpj(cnpj),
  }
}

export function isDuplicateParticipatingCompanyCnpj(
  companies: RaspadinhaParticipatingCompany[],
  cnpj: string,
  excludeId?: string
): boolean {
  const digits = normalizeParticipatingCompanyCnpj(cnpj)
  return companies.some(
    (company) =>
      company.id !== excludeId && normalizeParticipatingCompanyCnpj(company.cnpj) === digits
  )
}

export type AddParticipatingCompanyResult = {
  companies: RaspadinhaParticipatingCompany[]
  error: string | null
}

export function addParticipatingCompany(
  companies: RaspadinhaParticipatingCompany[],
  cnpjInput: string
): AddParticipatingCompanyResult {
  const digits = normalizeParticipatingCompanyCnpj(cnpjInput)

  if (digits.length !== 14) {
    return { companies, error: 'Informe um CNPJ com 14 dígitos.' }
  }

  if (!validateCnpj(cnpjInput)) {
    return { companies, error: 'Informe um CNPJ válido.' }
  }

  if (isDuplicateParticipatingCompanyCnpj(companies, cnpjInput)) {
    return { companies, error: 'Este CNPJ já está na lista.' }
  }

  return {
    companies: [...companies, createParticipatingCompany(cnpjInput)],
    error: null,
  }
}

export function removeParticipatingCompany(
  companies: RaspadinhaParticipatingCompany[],
  companyId: string
): RaspadinhaParticipatingCompany[] {
  return companies.filter((company) => company.id !== companyId)
}

export function ensureParticipatingCompanies(
  companies: RaspadinhaParticipatingCompany[] | undefined
): RaspadinhaParticipatingCompany[] {
  return (companies ?? []).map((company) => ({ ...company }))
}
